import { CardShell, SectionCard } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'
import { ConfirmDialog } from '@/components/confirm/ConfirmDialog'
import { InviteUserForm } from './InviteUserForm'
import { JobFunctionEditDialog } from './JobFunctionEditDialog'
import { RoleEditDialog } from './RoleEditDialog'
import { UserDirectory } from './UserDirectory'
import { describeUserMutationError, UserMutationKind } from './user-error'
import { useUsersPageState } from './useUsersPageState'

/**
 * SUPER_ADMIN user-management surface (ADR-0013). Container: fetches the
 * directory and orchestrates the four allowed operations (list, invite, role, status)
 * through the dedicated endpoints. Backend authorization stays authoritative — this
 * page is reachable only via a SUPER_ADMIN-gated route, and any backend 403 is still
 * handled by the shared auth-error machine. No user deletion, reset, resend, MFA,
 * or audit UI.
 */
export function UsersPage() {
	const s = useUsersPageState()

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-semibold">Users</h1>
					<p className="text-ds-text-muted">Provision and manage Atlas user accounts.</p>
				</div>
				<Button onPress={s.invite.toggle}>{s.invite.open ? 'Cancel' : 'Invite user'}</Button>
			</div>

			{s.invite.open && (
				<SectionCard title="Invite a user">
					<InviteUserForm
						onSubmit={s.invite.submit}
						isPending={s.invite.provision.isPending}
						errorMessage={
							s.invite.provision.isError
								? describeUserMutationError(s.invite.provision.error, UserMutationKind.PROVISION)
								: null
						}
					/>
				</SectionCard>
			)}

			<CardShell>
				<UserDirectory
					users={s.users.data ?? []}
					isLoading={s.users.isLoading}
					isError={s.users.isError}
					onEditRole={s.role.open}
					onEditJobFunction={s.jobFunction.open}
					onToggleStatus={s.status.open}
				/>
			</CardShell>

			<RoleEditDialog
				user={s.role.target}
				value={s.role.selected}
				onChange={s.role.setSelected}
				onConfirm={s.role.confirm}
				onCancel={s.role.close}
				isPending={s.role.changeRole.isPending}
				errorMessage={
					s.role.changeRole.isError ? describeUserMutationError(s.role.changeRole.error, UserMutationKind.ROLE) : null
				}
			/>

			<JobFunctionEditDialog
				user={s.jobFunction.target}
				value={s.jobFunction.selected}
				onChange={s.jobFunction.setSelected}
				onConfirm={s.jobFunction.confirm}
				onCancel={s.jobFunction.close}
				isPending={s.jobFunction.changeJobFunction.isPending}
				errorMessage={
					s.jobFunction.changeJobFunction.isError
						? describeUserMutationError(s.jobFunction.changeJobFunction.error, UserMutationKind.JOB_FUNCTION)
						: null
				}
			/>

			<ConfirmDialog
				open={!!s.status.target}
				title={s.status.target?.isActive ? 'Deactivate user' : 'Activate user'}
				description={
					s.status.target
						? `${s.status.target.isActive ? 'Deactivate' : 'Activate'} ${s.status.target.displayName} (${s.status.target.email})? Their role is preserved.`
						: ''
				}
				confirmLabel={s.status.target?.isActive ? 'Deactivate' : 'Activate'}
				confirmVariant={s.status.target?.isActive ? 'danger' : 'primary'}
				submitting={s.status.changeStatus.isPending}
				busyLabel="Working…"
				errorMessage={
					s.status.changeStatus.isError
						? describeUserMutationError(s.status.changeStatus.error, UserMutationKind.STATUS)
						: null
				}
				onConfirm={s.status.confirm}
				onCancel={s.status.close}
			/>
		</div>
	)
}
